'use client';

import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FeatureFlagVariant } from '@modules/feature-flag';
import { useFeatureFlag } from '@modules/feature-flag';
import { usePathname } from 'next/navigation';

import { NavLink } from '../NavLink/NavLink';

interface FlaggedNavLinkProps<T extends keyof FeatureFlagVariant> {
  workspaceId: string;
  flagName: T;
  flagFallback: FeatureFlagVariant[T];
  flagActiveValue: FeatureFlagVariant[T];
  icon: IconDefinition;
  label: string;
  href: string;
  /** Initial flag value from server-side fetch, used for SSR */
  initialFlagValue?: FeatureFlagVariant[T];
  /** Optional click handler */
  onClick?: () => void;
  /**
   * Optional feature discovery key.
   * Discovery indicator + ACTED mutation are handled by NavLink.
   */
  featureDiscoveryKey?: string;
}

export function FlaggedNavLink<T extends keyof FeatureFlagVariant>({
  workspaceId,
  flagName,
  flagFallback,
  flagActiveValue,
  icon,
  label,
  href,
  initialFlagValue,
  onClick,
  featureDiscoveryKey,
}: FlaggedNavLinkProps<T>) {
  const [flagValue] = useFeatureFlag(flagName, initialFlagValue ?? flagFallback);
  const pathname = usePathname();

  // Always show the link if it's active regardless of flag state
  const active = Boolean(pathname && pathname.startsWith(href));

  if (flagValue !== flagActiveValue && !active) {
    return null;
  }

  return (
    <NavLink
      leadingIcon={<FontAwesomeIcon icon={icon} />}
      label={label}
      href={`/${workspaceId}${href}`}
      onClick={onClick}
      featureDiscoveryKey={featureDiscoveryKey}
    />
  );
}
